import React, { useState, useCallback, useRef } from "react";
import {
    TextField,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Autocomplete,
    Box,
    Typography,
    Paper,
    Chip,
    CircularProgress,
    } from "../../ui";
import { Search,
    Restaurant,
    Add } from "../../ui/icons";
import { useDispatch,
    useSelector } from "react-redux";
import { snpAxios } from "../../global/api";
import {
  getAuthSession,
  GETQUICKITEMS,
  getPreUrlAccordingToCart,
  isEmpty,
  isFeatureAllowed,
  SUCCESS,
  STOCKMGMT,
  OfferType,
  isNumberEmpty
} from "../../../constants/constant";
import {
  addItemToBill,
  updateBillItem,
} from "../../../store/billDetailsSlice";
import { recalculateBillTotal } from "../../../store/thunk/billCalculatorThunk";
import DismissButton from "../../global/DismissButton";
import { useSnpUI } from "../../global/snpComponent/SnpUiProvider";
import utilities from "../../../utilities/utilities";
import { calculateRestoConversionPrice, getRestoAvailableStock, getRestoConversion,
  getRestoConversionSnapshot, isRestoStockQuantityAllowed } from "../restoBillingConversion";
import { getRestoItemLabel, getRestoItemName, getRestoRateCardName } from "../restoItemDisplay";

const ItemSearchBar = () => {
  const [showItemSearchDialog, setShowItemSearchDialog] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [foodItems, setFoodItems] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const debounceRef = useRef(null);
  const dispatch = useDispatch();
  const { snackbar } = useSnpUI();

  const storeActiveShopHash = useSelector(
    (state) => state.setActiveShopHashReducer.activeShopHash
  );
  const storeApiUrl = useSelector(
    (state) => state.setApiUrlReducer.MAINURL.INITIAL_BASE_URL
  );
  const { billItems, billDetails } = useSelector((state) => state.bill);

  const fetchFoodItems = useCallback(
    async (query, mode = 1) => {
      if (query.length < 2) {
        setFoodItems([]);
        return;
      }

      setIsSearching(true);
      try {
        const url =
          `${storeApiUrl.BACKEND_BASE_URL}${GETQUICKITEMS}?s=${utilities.encode(storeActiveShopHash.id)}&q=${encodeURIComponent(query)}&m=${mode}`;
        const response = await snpAxios.get(url);
        if (response.data.status === SUCCESS) {
          setFoodItems(response.data.jcJson.value_2 || []);
        } else {
          setFoodItems([]);
        }
      } catch (e) {
        console.error("ItemSearchBar fetchFoodItems error:", e);
        setFoodItems([]);
      }
      setIsSearching(false);
    },
    [storeApiUrl, storeActiveShopHash]
  );

const handleInputChange = (event, value, reason) => {
    setInputValue(value);
    if (reason !== 'input') return;  // <-- Skip API call for non-typing changes
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchFoodItems(value);
    }, 350);
};

  const handleSelectItem = (event, item) => {
    if (!item) return;
    setSelectedItem(null);
    setInputValue("");
    setFoodItems([]);

    if (billDetails.isPaid) {
      snackbar.show({
        message: "Can't add item to paid bill",
        color: "red",
      });
      return;
    }

    const prdId = item.id;
    const itemName = getRestoItemName(item);
    const existingItem = billItems.find((bi) => bi.prdId === prdId);
    const proposedQuantity = existingItem ? Number(existingItem.quantity) + 1 : 1;
    const stockItem = existingItem || { ...item, ...getRestoConversionSnapshot(item) };
    if (isFeatureAllowed(STOCKMGMT)
      && !isRestoStockQuantityAllowed(stockItem, proposedQuantity, stockItem.selectedUnit)) {
      const availableStock = getRestoAvailableStock(stockItem, stockItem.selectedUnit);
      snackbar.show({
        message: `Exceeds available stock (${availableStock} ${stockItem.selectedUnit || getRestoConversion(stockItem)?.unitName || ""})`,
        color: "red",
      });
      return;
    }

    if (existingItem) {
      const pricedItem = calculateRestoConversionPrice(existingItem,
        proposedQuantity, existingItem.selectedUnit);
      dispatch(
        updateBillItem({
          ...existingItem,
          ...pricedItem,
        })
      );
      snackbar.show({
        message: `${proposedQuantity} * ${itemName} added successfully`,
        color: "green",
        vertical: "top",
        horizontal: "right",
      });
    } else {
      const conversionSnapshot = getRestoConversionSnapshot(item);
      const pricedItem = calculateRestoConversionPrice({ ...item, ...conversionSnapshot }, 1,
        conversionSnapshot.selectedUnit);
      dispatch(
        addItemToBill({
          ...conversionSnapshot,
          ...pricedItem,
          prdId: item.id,
          name: itemName,
          combo: item.catgId === "snp_combo_id",
          offerDenom: item.offertype,
          offerAmt: item.offerAmt,
          cgst: item.cgst || 0,
          sgst: item.sgst || 0,
          priceIndex: 0,
          note: "",
        })
      );
      snackbar.show({
        message: `1 * ${itemName} added successfully`,
        color: "green",
        vertical: "top",
        horizontal: "right",
      });
    }

    dispatch(
      recalculateBillTotal({
        prevTotalPrice: existingItem ? Number(existingItem.finalPrice) : 0,
        newTotalPrice: existingItem
          ? calculateRestoConversionPrice(existingItem, Number(existingItem.quantity) + 1,
              existingItem.selectedUnit).finalPrice
          : calculateRestoConversionPrice({ ...item, ...getRestoConversionSnapshot(item) }, 1).finalPrice,
        cgstRate: item.cgst || 0,
        sgstRate: item.sgst || 0,
      })
    );
  };

  const handleDialogClose = () => {
    setShowItemSearchDialog(false);
    setInputValue("");
    setFoodItems([]);
    setSelectedItem(null);
  };

  const getProductLabel = (option) => {
    let label = getRestoItemLabel(option);
    if (option.sAmt !== undefined && option.sAmt !== null) label += ` - ₹${option.sAmt}`;
    return label;
  };

  return (
    <>
      <TextField
        fullWidth
        disabled={billDetails.isPaid}
        size="small"
        variant="outlined"
        placeholder="Search Food Item..."
        onClick={() => !billDetails.isPaid && setShowItemSearchDialog(true)}
        InputProps={{
          readOnly: true,
          startAdornment: (
            <InputAdornment position="start">
              <Restaurant
                fontSize="small"
                sx={{ color: "primary.main" }}
              />
            </InputAdornment>
          ),
        }}
        sx={{
          "& .UiOutlinedInput-root": {
            cursor: "pointer",
            fontSize: "13px",
            bgcolor: "background.paper",
            transition: "all 0.2s",
            "&:hover": {
              borderColor: "primary.main",
              boxShadow: "0 0 0 1px rgba(25, 118, 210, 0.1)",
            },
          },
        }}
      />
      <Dialog
        open={showItemSearchDialog}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, overflow: "hidden" },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: "16px",
            fontWeight: 600,
            textAlign: "center",
            pb: 0.5,
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.primary.main}08)`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
            <Restaurant sx={{ color: "primary.main", fontSize: 22 }} />
            Search Food Item
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5, fontWeight: 400 }}
          >
            Type at least 2 characters to search by name
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 1 }}>
          <Autocomplete
            fullWidth
            autoHighlight
            autoFocus
            openOnFocus
            value={selectedItem}
            onChange={handleSelectItem}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            options={foodItems}
            loading={isSearching}
            getOptionLabel={(option) => getProductLabel(option)}
            isOptionEqualToValue={(option, val) => option.id === val.id}
            noOptionsText={
              inputValue.length < 2
                ? "Type at least 2 characters to search"
                : "No items found"
            }
            PaperComponent={(props) => (
              <Paper
                {...props}
                sx={{
                  borderRadius: 2,
                  mt: 0.5,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              />
            )}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <Box
                  component="li"
                  key={key}
                  {...optionProps}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1.5,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    "&:last-child": { borderBottom: 0 },
                    "&:hover": {
                      bgcolor: "action.selected",
                    },
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={600} fontSize="14px">
                      {getRestoItemName(option)}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.5, mt: 0.25, flexWrap: "wrap" }}>
                      {getRestoRateCardName(option) && (
                        <Chip
                          label={getRestoRateCardName(option)}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: "10px", height: 20 }}
                        />
                      )}
                      {option.catgId === "snp_combo_id" && (
                        <Chip
                          label="Combo"
                          size="small"
                          color="secondary"
                          sx={{ fontSize: "10px", height: 20 }}
                        />
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: "right", ml: 1, flexShrink: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color="primary.main"
                      fontSize="15px"
                    >
                      ₹{option.sAmt || 0}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        mt: 0.25,
                      }}
                    >
                      <Add
                        fontSize="small"
                        sx={{ fontSize: 14, color: "success.main" }}
                      />
                      <Typography variant="caption" color="text.secondary" fontSize="10px">
                        Add to bill
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search by item name..."
                variant="outlined"
                size="medium"
                autoFocus
                sx={{
                  "& .UiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: "14px",
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "action.active", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <>
                      {isSearching && (
                        <CircularProgress color="primary" size={20} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <DismissButton
            variant="outlined"
            onClick={handleDialogClose}
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            Cancel
          </DismissButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ItemSearchBar;
